import {createContext, useContext, useEffect, useState} from 'react';
import {
  getDoc,
  getFirestore,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {useParams} from 'react-router';
import {ONE_HOUR_MS, TimeContext, useServerTime} from './TimeContext';
import {RoleFirebaseString} from 'types/role';
import type {CourseId} from 'types/common';
import {getCourseDocumentRef} from 'course/models/courseDocument';
import type { CourseFeature } from 'course/models/courseFeatures';

export interface CourseContextType {
  /**
   * The id of the course.
   */
  courseId: CourseId;
  /**
   * Whether the course data is loading.
   */
  isLoading: boolean;
  /**
   * The error that occurred when loading the course data (if any).
   */
  error: Error | null;
  /**
   * The (human-readable) name of the course.
   */
  courseName: string;
  /**
   * The start date of the course. Formatted as an ISO 8601 string as
   * YYYY-MM-DDTHH:mm:ssXXX
   */
  courseStartDate: string;
  /**
   * The end date of the course. Formatted as an ISO 8601 string as
   * YYYY-MM-DDTHH:mm:ssXXX
   */
  courseEndDate: string;
  /**
   * The number of weeks in the course.
   */
  nCourseWeeks: number;
  /**
   * The timestamp of the first section of the course. Section start times are
   * calculated from this timestamp and the section time delta. Note that the
   * timezone of this field may not be specified and is hardcoded to UTC-7
   * (PDT). Formatted as an ISO 8601 string as YYYY-MM-DDTHH:mm:ss-07:00
   */
  firstSectionTimestamp: string;
  /**
   * The number of hours of section a week. In other words, the difference in
   * hours between the start of the first section and the start of the last
   * section in a week.
   */
  sectionTimeDelta: number;
  /**
   * Whether a feature is enabled for the course, conditionally on the role.
   */
  isFeatureEnabled: (featureName: string, isSl?: boolean) => boolean;
  /**
   * Fetches the sections for the course.
   */
  getSectionsSnapshot: () => any;
  /**
   * Gets the first day of a section.
   */
  getFirstDayOfSection: (sectionIndex: number) => Date;
  /**
   * The features enabled for the course.
   */
  courseFeatures: CourseFeature[];
  /**
   * The features enabled for the section leaders.
   */
  slFeatures: string[];
  /**
   * The typesense index for the stories feature.
   */
  storiesTypesenseIndex: string;
  /**
   * The list of roles that have access to a roadmap for the course.
   */
  roadmapList: RoleFirebaseString[];
  /**
   * The link to use to authenticate with Canvas (if applicable).
   */
  courseCanvasAuthLink: string;
  /**
   * TODO: Remove this field. Only used by CS 105 to determine which instructors
   * are in the course. Instead, instructors should be fetched from a new
   * field.
   */
  isCodeInPlaceCourse: boolean;
  /**
   * Whether Karel is used in the course.
   */
  usesKarel: boolean;
  /**
   * Whether TeachNow is used in the course.
   */
  usesTeachNow: boolean;
  /**
   * Whether the course has grades.
   */
  courseHasGrades: boolean;
  /**
   * Whether the course is a Foothill College course.
   */
  isFoothill: boolean;
  /**
   * Whether the section leader onboarding is closed.
   */
  isSLOnboardingClosed: boolean;
  /**
   * Sets whether the section leader onboarding is closed.
   */
  setIsSLOnboardingClosed: (isOnboardingClosed: boolean) => void;
  /**
   * 
   */
  minorSectionCap: number;
  nonMinorSectionCap: number;

}

const defaultData: CourseContextType = {
  courseId: '',
  courseName: '',
  courseStartDate: '',
  courseEndDate: '',
  nCourseWeeks: 0,
  isLoading: true,
  error: null,
  firstSectionTimestamp: '',
  courseFeatures: [],
  sectionTimeDelta: 0,
  slFeatures: [],
  isFeatureEnabled: (featureName: string, isSl?: boolean) => false,
  getSectionsSnapshot: () => {
    return null;
  },
  getFirstDayOfSection: (sectionIndex: number) => {
    return null;
  },
  storiesTypesenseIndex: '',
  roadmapList: [],
  courseCanvasAuthLink: '',
  isCodeInPlaceCourse: false,
  usesKarel: false,
  usesTeachNow: false,
  courseHasGrades: false,
  isFoothill: false,
  isSLOnboardingClosed: false,
  setIsSLOnboardingClosed: (isOnboardingClosed: boolean) => {
    return;
  },
  minorSectionCap: 0,
  nonMinorSectionCap: 0,
};

/**
 * TODO: there are two big refactors (one easy, one hard)
 *
 * 1. in some places in the database, weeks are 0 indexed, in others they are 1 indexed.
 *    This should be consistent (likely 0 indexed). When displaying to the user, we can add 1
 *
 * 2. This context uses a long list of useStates (each with their one setState). There is a
 *    great chance to reduce the amount of code.
 */

export const CourseContext = createContext<CourseContextType>(defaultData);

/**
 * Reads and parses course data for the given courseId.
 */
export function useCourseContextForCourse(
  courseId: CourseId,
): CourseContextType {

  const [courseName, setCourseName] = useState<string>('');
  const [courseStartDate, setCourseStartDate] = useState<string>('');
  const [courseEndDate, setCourseEndDate] = useState<string>('');
  const [nCourseWeeks, setnCourseWeeks] = useState<number>(0);
  const [firstSectionTimestamp, setFirstSectionTimeStamp] =
    useState<string>('');
  const [courseCanvasAuthLink, setCanvasCourseAuthLink] = useState<string>('');
  const [courseHasGrades, setCourseHasGrades] = useState<boolean>(false);
  const [courseFeatures, setCourseFeatures] = useState<CourseFeature[]>([]);
  const [slFeatures, setSlFeatures] = useState<string[]>([]);
  const [sectionTimeDelta, setSectionTimeDelta] = useState<number>(0);
  const [minorSectionCap, setMinorSectionCap] = useState<number>(0);
  const [nonMinorSectionCap, setNonMinorSectionCap] = useState<number>(0);

  
  const [storiesTypesenseIndex, setStoriesTypesenseIndex] =
    useState<string>('');
  const [roadmapList, setRoadmapList] = useState<RoleFirebaseString[]>([
    'student',
    'sl',
  ]);
  const [isCodeInPlaceCourse, setIsCodeInPlaceCourse] =
    useState<boolean>(false);
  const [usesKarel, setUsesKarel] = useState<boolean>(false);
  const [usesTeachNow, setUsesTeachNow] = useState<boolean>(false);
  const isFoothill = ['foothill-cs49', 'cs49-f24', 'cs49-w24', 'cs49-s25'].includes(
    courseId,
  );
  const [isSLOnboardingClosed, setIsSLOnboardingClosed] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const db = getFirestore();
  const courseDocRef = getCourseDocumentRef(courseId);

  const getFirstDayOfSection = (sectionIndex: number) => {
    // NOTE: when calling this function, make sure to check if firstSectionTimestamp is loaded or use it in a useEffect
    if (!firstSectionTimestamp) {
      throw new Error('Timestamp not loaded yet');
    }
    const firstSectionOfCourseDate = new Date(firstSectionTimestamp);
    const firstSectionForTimeIndex = new Date(firstSectionOfCourseDate.getTime() + sectionIndex * ONE_HOUR_MS);
    // add the timezone offset
    return firstSectionForTimeIndex;
  };



  
  useEffect(() => {
    const getCourseData = async () => {
      if (!courseId) return;
      console.log("SETTING IS LOADING TO TRUE")

      setIsLoading(true);
      setError(null);
      const courseDoc = await getDoc(courseDocRef);
      if (courseDoc.exists()) {
        const courseData = courseDoc.data();
        const safeCourseFeatures = courseData.features ?? [];
        const safeSlFeatures = courseData.slFeatures ?? [];
        setCourseName(courseData.name ?? '');
        setCourseStartDate(courseData.startDate ?? '');
        setnCourseWeeks(courseData.nWeeks ?? 0);
        // TODO: this is a hack to get the first section timestamp in the
        // correct timezone. We should remove this once we have a better
        // solution.
        setFirstSectionTimeStamp(
          courseData.firstSectionTimestamp
            ? `${courseData.firstSectionTimestamp}-07:00`
            : '',
        );
        setCourseEndDate(courseData.endDate ?? '');
        setCourseFeatures(safeCourseFeatures);
        setSlFeatures(safeSlFeatures);
        try {
          setSectionTimeDelta(Number(courseData.sectionTimeDelta) ?? 0); // the number of hours of section a week
        } catch (e) {
          console.error('Error setting sectionTimeDelta', {
            courseData,
            e,
          });
          setSectionTimeDelta(0);
        }
        setStoriesTypesenseIndex(courseData.storiesTypesenseIndex ?? '');
        setCanvasCourseAuthLink(courseData.canvasAuthLink ?? '');
        setIsSLOnboardingClosed(courseData.isSLOnboardingClosed ?? false);
        setCourseHasGrades(safeSlFeatures.includes('grades'));
        const verboseRoadmapList = [
          'student',
          'sl',
          ...(courseData.roadmapList ?? ['student', 'sl']),
        ];
        const uniqueRoadmap = array =>
          array.filter((v, i) => array.indexOf(v) === i);
        setRoadmapList(uniqueRoadmap(verboseRoadmapList));
        setIsCodeInPlaceCourse(courseData.isCodeInPlaceCourse || true);
        setUsesKarel(courseData.usesKarel || true);
        setUsesTeachNow(courseData.usesTeachNow || true);
        setMinorSectionCap(courseData.minorSectionCap || 10);
        setNonMinorSectionCap(courseData.nonMinorSectionCap || 10);
      }
      setIsLoading(false);
    };
    getCourseData().catch((e) => {
      setError(e);

      setIsLoading(false);
    });
  }, [courseId]);


  const isFeatureEnabled = (featureName: string, isSl: boolean = false) => {
    const features = isSl ? slFeatures : courseFeatures;
    return features.includes(featureName);
  };

  const getSectionsSnapshot = async () => {
    // fetches sections for this course
    const sectionRef = collection(db, 'sections');
    const q = query(sectionRef, where('courseId', '==', courseId));
    const querySnapshot = await getDocs(q);
    return querySnapshot;
  };


  console.log("isLoading", isLoading)

  return {
    courseId,
    isLoading,
    error,
    courseName,
    courseStartDate,
    courseEndDate,
    nCourseWeeks,
    firstSectionTimestamp,
    courseFeatures,
    slFeatures,
    isFeatureEnabled,
    getSectionsSnapshot,
    sectionTimeDelta,
    getFirstDayOfSection,
    storiesTypesenseIndex,
    roadmapList,
    courseCanvasAuthLink,
    isCodeInPlaceCourse,
    usesKarel,
    usesTeachNow,
    courseHasGrades,
    isFoothill,
    isSLOnboardingClosed,
    setIsSLOnboardingClosed,
    minorSectionCap,
    nonMinorSectionCap,
  };
}

/**
 * Provides a CourseContext for the user's current course.
 * This is a wrapper around the useCourseContextForCourse hook.
 */
export function CourseProvider({children}: {children: React.ReactNode}) {
  const {courseId} = useParams();
  const courseContext = useCourseContextForCourse(courseId);

  return (
    <CourseContext.Provider value={courseContext}>
      {children}
    </CourseContext.Provider>
  );
}



/**
 * Calculates the timestamp of when the last section of the course starts.
 * @param firstSectionTimestamp The timestamp of the first section of the course.
 * @param sectionTimeDelta The number of hours of section a week.
 * @returns The timestamp of when the last section of the course starts.
 */
export const calcLastSectionTimestamp = (
  firstSectionTimestamp: string,
  sectionTimeDelta: number,
): string => {
  if (!firstSectionTimestamp) {
    return new Date().toISOString();
  }

  try {
    const startDate = new Date(firstSectionTimestamp);
    const deltaHours = sectionTimeDelta * 60 * 60 * 1000;
    const lastSectionTimestamp = new Date(startDate.getTime() + deltaHours);
    return lastSectionTimestamp.toISOString();
  } catch (e) {
    console.error('Error calculating last section timestamp', {
      firstSectionTimestamp,
      sectionTimeDelta,
    });
    return new Date().toISOString();
  }
};
